import { Build } from '@stencil/core';

import PubSub from './pub-sub';
import { getPlacementFromStore, setPlacementToStore } from './store';
import { AffiliatePossibleCharacteristics } from '../../../types/base/affiliate/characteristics';
import { AffiliatePlacement } from '../../../types/base/affiliate/placement';
import { AffiliateProperty } from '../../../types/base/affiliate/property';
import { AffiliateControllerResponse } from '../../../types/controllers/affiliate';
import { getQueryParamString } from '../../../util/string';
import { PlacementParams } from '../types';

import { getBaseUrl, getQueryParamFromUrl, isValidPaidMarketingParam, requiredParamsCheck, formatPromotions } from '.';

const pubSub = new PubSub();

export const fetchPlacement = (
    params: {
        placement_id: Extract<AffiliatePlacement['id'], number>;
        property_id?: Extract<AffiliateProperty['id'], number>;
    } & AffiliatePossibleCharacteristics,
    devMode: boolean
): Promise<AffiliateControllerResponse> => {
    const { placement_id: placementId, property_id: propertyId = 1, ...restParams } = params;
    const query = getQueryParamString(restParams);

    const baseUrl = getBaseUrl(devMode);
    const url = `${baseUrl}/v1/affiliate/properties/${propertyId}/placements/${placementId}/promotions${query}`;

    // eslint-disable-next-line no-console
    const fetchErr = (res: Response) => !Build.isTesting && console.error(res);
    return fetch(url)
        .then(res => {
            if (!res.ok) {
                return fetchErr(res);
            }

            return res.status !== 204 && res.json();
        })
        .catch(fetchErr);
};

export async function getPlacement(
    params: PlacementParams,
    cb?: (placement: AffiliateControllerResponse, location: string) => void,
    devMode = false,
    componentType?: string,
    ignorePubsub?: boolean
) {
    const hasMissingParams = requiredParamsCheck(
        {
            property_id: params.property_id,
            placement_id: params.placement_id,
        },
        componentType
    );

    if (hasMissingParams) {
        return;
    }

    const { location } = params;
    const paidMarketing = getQueryParamFromUrl('paidMarketing');

    if (paidMarketing && isValidPaidMarketingParam(paidMarketing)) {
        params.paid_marketing = paidMarketing;
    }

    const sortedParams = {};
    Object.keys(params)
        .sort()
        .forEach(key => (sortedParams[key] = params[key]));

    // if ignorePubsub, do the fetch and return the placement
    if (ignorePubsub === true) {
        const placementData = await fetchPlacement(params, devMode);
        const locationToUse = location || placementData.location;
        cb ? cb(placementData, locationToUse) : (this.placement = placementData);
    }

    const placementKey = getQueryParamString(sortedParams);
    let placement = getPlacementFromStore(placementKey);

    if (!placement) {
        // CASE -- placement does not exist in the store
        // set isLoading to true so other components don't fetch as well
        placement = { isLoading: true, data: null };
        setPlacementToStore(placementKey, placement);
        const placementData = await fetchPlacement(params, devMode);

        // If request failed, placement doesn't exist, or it is inactive then set isLoading to false and do nothing
        if (!placementData || !placementData.is_active) {
            setPlacementToStore(placementKey, { ...placement, isLoading: false });
            return;
        }

        // publish the fetched data to the pubSub
        pubSub.publish(placementKey, placementData);

        // update the placement data and set isLoading to false
        // then update the placement in the store
        placement = { isLoading: false, data: placementData };
        setPlacementToStore(placementKey, placement);

        const locationToUse = location || placement.data.location;
        cb ? cb(placement.data, locationToUse) : (this.placement = placement.data);
    } else {
        // CASE -- placement exists in the store
        if (!placement.isLoading) {
            // CASE -- the placement is not loading
            // if data doesn't exist or is inactive then early return
            if (!placement.data || !placement.data.is_active) {
                return;
            }
            const locationToUse = location || placement.data.location;
            cb ? cb(placement.data, locationToUse) : (this.placement = placement.data);
        } else {
            // CASE -- the placement is loading
            // subscribe to the pubSub with the placementKey
            pubSub.subscribe(placementKey, placementData => {
                // unsubscribe from the pubSub after data is published
                pubSub.unsubscribe(placementKey);

                const locationToUse = location || placementData.location;
                cb ? cb(placementData, locationToUse) : (this.placement = placementData);
            });
        }
    }
}

export async function fetchPromo(propertyId: number, promotionId: number, context: string, devMode: boolean) {
    try {
        const baseUrl = getBaseUrl(devMode);
        const url = `${baseUrl}/v1/affiliate/properties/${propertyId}/promotions/${promotionId}${
            context ? `?context=${context}` : ''
        }`;

        const res = await fetch(url);
        const response = await res.json();

        if (response.error) {
            // eslint-disable-next-line no-console
            console.error(`BAM Error fetching promotion: ${response.error}`);
            return;
        }

        const formattedPromo = formatPromotions([response.promotion], {
            allowAltLink: true,
            promotionListId: null,
            location: response.location ?? null,
            context: context,
            propertyId: Number(propertyId),
        })[0];

        return {
            promotion: formattedPromo,
            location: response.location ?? null,
        };
    } catch (error) {
        // eslint-disable-next-line no-console
        console.error(`BAM Error fetching promotion: ${error}`);
    }
}

export async function fetchFeaturedPromo(propertyId: number, affiliateId: number, context: string, devMode: boolean) {
    try {
        const baseUrl = getBaseUrl(devMode);
        const url = `${baseUrl}/v1/affiliate/properties/${propertyId}/affiliates/${affiliateId}/promotions/featured${
            context ? `?context=${context}` : ''
        }`;

        const res = await fetch(url);
        const response = await res.json();

        if (response.error) {
            // eslint-disable-next-line no-console
            console.error(`BAM Error fetching promotion: ${response.error}`);
            return;
        }

        const formattedPromo = formatPromotions([response.promotion], {
            allowAltLink: true,
            promotionListId: null,
            location: null,
            context: context,
            propertyId: Number(propertyId),
        })[0];

        return formattedPromo;
    } catch (error) {
        // eslint-disable-next-line no-console
        console.error(`BAM Error fetching promotion: ${error}`);
    }
}

export async function fetchTrendingParlays(devMode = false) {
    const url = `${getBaseUrl(devMode)}/v1/data/parlays/trending`;
    const res = await fetch(url);
    if (!res.ok) {
        throw new Error(`Request failed: ${res.status}`);
    }

    return res.json();
}

export async function fetchTrendingParlayOdds(lineIds: string[], devMode = false) {
    const url = `${getBaseUrl(devMode)}/v1/data/parlays/odds`;
    const query = encodeURIComponent(lineIds.join(','));
    const res = await fetch(`${url}?marketIds=${query}`);
    if (!res.ok) {
        return null;
    }

    return res.json();
}

export type PlayerPropsApiProp = {
    name: string;
    displayValue: string;
    value?: string | number;
    odds?: {
        over?: string;
        under?: string;
        overDeeplinkId?: string;
        underDeeplinkId?: string;
        yes?: string;
        no?: string;
        yesDeeplinkId?: string;
        noDeeplinkId?: string;
        price?: string;
        priceDeeplinkId?: string;
    };
};

export type PlayerPropsApiStat = {
    name: string;
    value: string | number;
};

export type PlayerPropsApiResponse = {
    name: string;
    error?: string;
    team?: string;
    teamLogo?: string;
    headshot?: string;
    position?: string;
    nextMatchup?: {
        opponent?: string;
        eventId?: string | number;
        startTime?: string;
        isHome?: boolean;
    };
    props?: PlayerPropsApiProp[];
    stats?: PlayerPropsApiStat[];
    disclaimer?: Record<string, string> | null;
};

export async function fetchPlayerProps(
    params: { playerName: string; league: string },
    devMode = false
): Promise<PlayerPropsApiResponse> {
    const playerName = `${params.playerName || ''}`.trim();
    const league = `${params.league || ''}`.trim().toLowerCase();

    if (!playerName || !league) {
        throw new Error('playerName and league are required');
    }

    const url = new URL(`${getBaseUrl(devMode)}/v1/data/props/player`);
    url.searchParams.set('playerName', playerName);
    url.searchParams.set('league', league);

    const res = await fetch(url.toString());
    if (!res.ok) {
        throw new Error(`Request failed: ${res.status}`);
    }

    return res.json();
}

export type MatchupPropsApiOdds = {
    price?: string;
    priceDeeplinkId?: string;
    over?: string;
    under?: string;
    overDeeplinkId?: string;
    underDeeplinkId?: string;
};

export type MatchupPropsApiTeam = {
    id?: string | number;
    name?: string;
    side?: string;
    abbreviation?: string;
    logo?: string;
    record?: string;
    spread?: {
        value?: number;
        odds?: MatchupPropsApiOdds;
    };
    moneyline?: {
        odds?: MatchupPropsApiOdds;
    };
};

export type MatchupPropsApiBook = {
    sportsbookId: string;
    total?: {
        value?: number;
        odds?: MatchupPropsApiOdds;
    };
    teams: Array<{
        id?: string | number;
        side?: string;
        spread?: {
            value?: number;
            odds?: MatchupPropsApiOdds;
        };
        moneyline?: {
            odds?: MatchupPropsApiOdds;
        };
    }>;
};

export type MatchupPropsApiResponse = {
    error?: string;
    league: string;
    sport?: string;
    eventId?: string | number;
    eventName?: string;
    startTime?: string;
    total?: {
        value?: number;
        odds?: MatchupPropsApiOdds;
    };
    teams: MatchupPropsApiTeam[];
    disclaimer?: Record<string, string> | null;
    /** Returned only when sportsbookIds are requested. */
    books?: Record<string, MatchupPropsApiBook>;
};

export async function fetchMatchupProps(
    params: { league: string; teamName?: string; sportsbookIds?: Array<number | string> },
    devMode = false
): Promise<MatchupPropsApiResponse> {
    const league = `${params.league || ''}`.trim().toLowerCase();
    const teamName = `${params.teamName || ''}`.trim();
    const sportsbookIds = (params.sportsbookIds || []).map(id => `${id || ''}`.trim()).filter(id => /^\d+$/.test(id));

    if (!league) {
        throw new Error('league is required');
    }

    const url = new URL(`${getBaseUrl(devMode)}/v1/data/props/matchup`);
    url.searchParams.set('league', league);
    if (teamName) {
        url.searchParams.set('teamName', teamName);
    }
    if (sportsbookIds.length) {
        url.searchParams.set('sportsbookIds', sportsbookIds.join(','));
    }

    const res = await fetch(url.toString());
    if (!res.ok) {
        throw new Error(`Request failed: ${res.status}`);
    }

    return res.json();
}

export type TeamPropsApiProp = {
    name: string;
    displayValue: string;
    value?: string | number;
    odds?: {
        over?: string;
        under?: string;
        overDeeplinkId?: string;
        underDeeplinkId?: string;
        yes?: string;
        no?: string;
        yesDeeplinkId?: string;
        noDeeplinkId?: string;
        price?: string;
        priceDeeplinkId?: string;
    };
};

export type TeamPropsApiStat = {
    name: string;
    value: string | number;
};

export type TeamPropsApiResponse = {
    name: string;
    error?: string;
    sport?: string;
    teamLogo?: string;
    abbreviation?: string;
    record?: string;
    nextMatchup?: {
        opponent?: string;
        eventId?: string | number;
        startTime?: string;
        isHome?: boolean;
    };
    props?: TeamPropsApiProp[];
    stats?: TeamPropsApiStat[];
    disclaimer?: Record<string, string> | null;
};

export async function fetchTeamProps(
    params: { teamName: string; league: string },
    devMode = false
): Promise<TeamPropsApiResponse> {
    const teamName = `${params.teamName || ''}`.trim();
    const league = `${params.league || ''}`.trim().toLowerCase();

    if (!teamName || !league) {
        throw new Error('teamName and league are required');
    }

    const url = new URL(`${getBaseUrl(devMode)}/v1/data/props/team`);
    url.searchParams.set('teamName', teamName);
    url.searchParams.set('league', league);

    const res = await fetch(url.toString());
    if (!res.ok) {
        throw new Error(`Request failed: ${res.status}`);
    }

    return res.json();
}

export async function fetchPicks(params: { leagueId?: string | number; teamName?: string } = {}, devMode = false) {
    const search = new URLSearchParams();
    if (params.leagueId !== undefined && params.leagueId !== null && `${params.leagueId}` !== '') {
        search.set('leagueId', `${params.leagueId}`);
    }
    if (params.teamName) {
        search.set('teamName', params.teamName);
    }

    const query = search.toString();
    const url = `${getBaseUrl(devMode)}/v1/data/picks${query ? `?${query}` : ''}`;
    const res = await fetch(url);
    if (!res.ok) {
        throw new Error(`Request failed: ${res.status}`);
    }

    return res.json();
}

const betOptionTypeTranslationsRequestCache = new Map<string, Promise<Record<string, string>>>();
const teamTranslationRequestCache = new Map<string, Promise<string | null>>();

const asString = (value: unknown) => {
    if (typeof value !== 'string') {
        return '';
    }

    const trimmed = value.trim();
    return trimmed;
};

const pickFirstString = (obj: Record<string, unknown> | null | undefined, keys: string[]) => {
    if (!obj || typeof obj !== 'object') {
        return '';
    }

    for (const key of keys) {
        const str = asString(obj[key]);
        if (str) {
            return str;
        }
    }

    return '';
};

const normalizeBetOptionTypeTranslations = (payload: unknown): Record<string, string> => {
    if (!payload) {
        return {};
    }

    const normalized: Record<string, string> = {};
    const visited = new Set<object>();
    const sourceKeys = ['value', 'name', 'defaultValue', 'defaultName', 'englishValue', 'englishName', 'source', 'key'];
    const translatedKeys = [
        'translation',
        'translatedValue',
        'translatedName',
        'localizedValue',
        'localizedName',
        'localName',
        'displayValue',
        'displayName',
    ];

    const addRecord = (obj: Record<string, unknown>, keyPrefix?: string) => {
        const source = pickFirstString(obj, sourceKeys);
        const translated = pickFirstString(obj, translatedKeys);
        const resolvedValue = translated || source;

        if (!resolvedValue) {
            return;
        }

        const id = obj.id ?? obj.betOptionTypeId ?? obj.optionTypeId;
        const idKey = id !== undefined && id !== null ? `id:${id}` : '';
        const scopedIdKey = keyPrefix && id !== undefined && id !== null ? `${keyPrefix}:id:${id}` : '';

        if (source && source !== resolvedValue) {
            normalized[source] = resolvedValue;
            normalized[source.toLowerCase()] = resolvedValue;
        }

        if (idKey) {
            normalized[idKey] = resolvedValue;
        }

        if (scopedIdKey) {
            normalized[scopedIdKey] = resolvedValue;
        }
    };

    const walk = (value: unknown) => {
        if (!value) {
            return;
        }

        if (Array.isArray(value)) {
            value.forEach(item => walk(item));
            return;
        }

        if (typeof value !== 'object') {
            return;
        }

        if (visited.has(value as object)) {
            return;
        }

        visited.add(value as object);

        const obj = value as Record<string, unknown>;

        if (Array.isArray(obj.results)) {
            walk(obj.results);
        }

        if (Array.isArray(obj.data)) {
            walk(obj.data);
        }

        if (Array.isArray(obj.betOptionTypes)) {
            walk(obj.betOptionTypes);
        }

        const entries = Object.entries(obj);
        if (entries.length > 0 && entries.every(([, entryValue]) => typeof entryValue === 'string')) {
            entries.forEach(([key, entryValue]) => {
                const translation = asString(entryValue);
                if (!translation) {
                    return;
                }

                normalized[key] = translation;
                normalized[key.toLowerCase()] = translation;
            });
            return;
        }

        addRecord(obj);

        if (obj.linePeriodType && typeof obj.linePeriodType === 'object' && !Array.isArray(obj.linePeriodType)) {
            addRecord(obj.linePeriodType as Record<string, unknown>, 'linePeriodType');
        }

        walk(obj.betType);
        walk(obj.lineType);
        walk(obj.linePeriodType);
        walk(obj.optionType);
        walk(obj.sideOptionType);
        walk(obj.sideOptionTypes);
    };

    walk(payload);
    return normalized;
};

const extractTeamNameTranslation = (payload: unknown): string | null => {
    if (!payload) {
        return null;
    }

    if (typeof payload === 'string') {
        const value = payload.trim();
        return value || null;
    }

    if (Array.isArray(payload)) {
        return extractTeamNameTranslation(payload[0]);
    }

    if (typeof payload === 'object') {
        const obj = payload as Record<string, unknown>;

        if (obj.data) {
            return extractTeamNameTranslation(obj.data);
        }

        if (!Array.isArray(obj.results)) {
            return null;
        }

        const translated = pickFirstString(obj?.results[0], [
            'translation',
            'translatedName',
            'localizedName',
            'localName',
            'displayName',
            'name',
            'teamName',
            'value',
        ]);

        if (translated) {
            return translated;
        }
    }

    return null;
};

export function fetchBetOptionTypeTranslations(languageCode: string, devMode = false): Promise<Record<string, string>> {
    const normalizedLanguage = `${languageCode || ''}`.trim().toLowerCase();
    if (!normalizedLanguage) {
        return Promise.resolve({});
    }

    const cacheKey = `${devMode ? 'dev' : 'prod'}:${normalizedLanguage}`;
    if (!betOptionTypeTranslationsRequestCache.has(cacheKey)) {
        const request = (async () => {
            const url = `${getBaseUrl(devMode)}/v1/data/translations/bet-option-types`;
            const res = await fetch(`${url}?languageCode=${encodeURIComponent(normalizedLanguage)}`);

            if (!res.ok) {
                return {};
            }

            const json = await res.json();
            return normalizeBetOptionTypeTranslations(json);
        })();

        betOptionTypeTranslationsRequestCache.set(cacheKey, request);
    }

    return betOptionTypeTranslationsRequestCache.get(cacheKey) as Promise<Record<string, string>>;
}

export function fetchTeamTranslation(teamId: number, languageCode: string, devMode = false): Promise<string | null> {
    if (!Number.isFinite(teamId)) {
        return Promise.resolve(null);
    }

    const normalizedLanguage = `${languageCode || ''}`.trim().toLowerCase();
    if (!normalizedLanguage) {
        return Promise.resolve(null);
    }

    const cacheKey = `${devMode ? 'dev' : 'prod'}:${teamId}:${normalizedLanguage}`;
    if (!teamTranslationRequestCache.has(cacheKey)) {
        const request = (async () => {
            const url = `${getBaseUrl(devMode)}/v1/data/translations/team/${encodeURIComponent(teamId)}`;
            const res = await fetch(`${url}?languageCode=${encodeURIComponent(normalizedLanguage)}`);

            if (!res.ok) {
                return null;
            }

            const json = await res.json();
            return extractTeamNameTranslation(json);
        })();

        teamTranslationRequestCache.set(cacheKey, request);
    }

    return teamTranslationRequestCache.get(cacheKey) as Promise<string | null>;
}
